import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Command } from 'lucide-react';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  Package,
  Upload,
  TrendingUp,
  Settings,
  Shield,
  Database,
  Brain,
  Trash2,
  FileText,
  ShoppingCart,
  Utensils,
  Warehouse,
  ArrowRightLeft,
  BarChart3,
  Users,
  FolderTree,
} from 'lucide-react';

interface CommandItem {
  name: string;
  href: string;
  icon: any;
  category: string;
  keywords: string[];
}

const commands: CommandItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, category: 'Core', keywords: ['home', 'overview'] },
  { name: 'Categories', href: '/categories', icon: FolderTree, category: 'Core', keywords: ['organize', 'hierarchy', 'folders'] },
  { name: 'Items & Recipes', href: '/items', icon: Package, category: 'Core', keywords: ['products', 'ingredients', 'bom'] },
  { name: 'Sales Import', href: '/imports', icon: Upload, category: 'Core', keywords: ['upload', 'data', 'csv'] },
  { name: 'Consumption', href: '/consumption', icon: TrendingUp, category: 'Core', keywords: ['usage', 'stock'] },

  { name: 'Procurement Hub', href: '/procurement', icon: ShoppingCart, category: 'Procurement', keywords: ['purchasing', 'orders'] },
  { name: 'Suppliers', href: '/procurement/suppliers', icon: Users, category: 'Procurement', keywords: ['vendors', 'contacts'] },
  { name: 'Purchase Invoices', href: '/procurement/invoices', icon: FileText, category: 'Procurement', keywords: ['bills', 'payments'] },
  { name: 'Supplier Contracts', href: '/supplier-contracts', icon: FileText, category: 'Procurement', keywords: ['agreements', 'pricing'] },
  { name: 'Receiving Queue', href: '/receiving', icon: Package, category: 'Procurement', keywords: ['deliveries', 'incoming'] },

  { name: 'POS & Warehouses', href: '/pos-warehouse', icon: Warehouse, category: 'Warehouses', keywords: ['locations', 'storage'] },
  { name: 'Transfer Requests', href: '/transfer-requests', icon: ArrowRightLeft, category: 'Warehouses', keywords: ['moves', 'relocate'] },
  { name: 'Warehouse Analytics', href: '/warehouse-analytics', icon: BarChart3, category: 'Warehouses', keywords: ['reports', 'insights'] },

  { name: 'AI Menu Import', href: '/menu-import', icon: Utensils, category: 'AI Features', keywords: ['automation', 'ocr'] },
  { name: 'Recipe Review', href: '/recipe-review', icon: Brain, category: 'AI Features', keywords: ['verification', 'check'] },
  { name: 'Predictive Inventory', href: '/predictive-inventory', icon: Brain, category: 'AI Features', keywords: ['forecast', 'predict'] },
  { name: 'Waste Reduction', href: '/waste-reduction', icon: Trash2, category: 'AI Features', keywords: ['optimization', 'savings'] },
  { name: 'Cost Analytics', href: '/procurement/cost-analytics', icon: BarChart3, category: 'AI Features', keywords: ['analysis', 'spending'] },

  { name: 'Integrations', href: '/integrations', icon: Database, category: 'System', keywords: ['api', 'connect', 'mydata', 'erp'] },
  { name: 'GDPR & Privacy', href: '/privacy', icon: Shield, category: 'System', keywords: ['compliance', 'security'] },
  { name: 'Settings', href: '/settings', icon: Settings, category: 'System', keywords: ['preferences', 'configuration'] },
];

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredCommands = query
    ? commands.filter(
        (cmd) =>
          cmd.name.toLowerCase().includes(query.toLowerCase()) ||
          cmd.category.toLowerCase().includes(query.toLowerCase()) ||
          cmd.keywords.some((kw) => kw.toLowerCase().includes(query.toLowerCase()))
      )
    : commands;

  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.category]) {
      acc[cmd.category] = [];
    }
    acc[cmd.category].push(cmd);
    return acc;
  }, {} as Record<string, CommandItem[]>);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          onClose();
        }
      }

      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredCommands.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          navigate(filteredCommands[selectedIndex].href);
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, selectedIndex, filteredCommands, navigate]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center pt-32 z-50">
      <div className="glass-panel w-full max-w-2xl max-h-[600px] flex flex-col">
        <div className="flex items-center gap-3 p-4 border-b border-white/10">
          <Search className="w-5 h-5 text-white/40" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder="Search commands or navigate..."
            className="flex-1 bg-transparent text-white placeholder-white/40 outline-none text-lg"
          />
          <div className="flex items-center gap-1 text-xs text-white/40">
            <kbd className="px-2 py-1 bg-white/5 border border-white/10 rounded">
              <Command className="w-3 h-3" />
            </kbd>
            <kbd className="px-2 py-1 bg-white/5 border border-white/10 rounded">K</kbd>
          </div>
        </div>

        <div className="overflow-y-auto p-2">
          {Object.entries(groupedCommands).length === 0 ? (
            <div className="p-8 text-center text-white/60">
              No results found for "{query}"
            </div>
          ) : (
            Object.entries(groupedCommands).map(([category, items], categoryIndex) => {
              const categoryStartIndex = Object.entries(groupedCommands)
                .slice(0, categoryIndex)
                .reduce((sum, [, items]) => sum + items.length, 0);

              return (
                <div key={category} className="mb-4">
                  <div className="px-3 py-2 text-xs font-semibold text-white/50 uppercase tracking-wider">
                    {category}
                  </div>
                  <div className="space-y-1">
                    {items.map((cmd, index) => {
                      const globalIndex = categoryStartIndex + index;
                      const isSelected = globalIndex === selectedIndex;

                      return (
                        <button
                          key={cmd.href}
                          onClick={() => {
                            navigate(cmd.href);
                            onClose();
                          }}
                          onMouseEnter={() => setSelectedIndex(globalIndex)}
                          className={clsx(
                            'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left',
                            isSelected
                              ? 'bg-primary-500/30 border border-primary-400/50'
                              : 'hover:bg-white/5'
                          )}
                        >
                          <cmd.icon className="w-5 h-5 text-primary-400" />
                          <span className="flex-1 text-white">{cmd.name}</span>
                          {isSelected && (
                            <span className="text-xs text-white/40">↵</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="border-t border-white/10 p-3 flex items-center justify-between text-xs text-white/40">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded">↑</kbd>
              <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded">↓</kbd>
              Navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded">↵</kbd>
              Select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-white/5 border border-white/10 rounded">ESC</kbd>
              Close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
